import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-6 bg-slate-50/50" data-testid="reset-no-token">
        <div className="w-full max-w-md mec-card p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-amber-600" />
          </div>
          <h2 className="font-display text-xl font-bold text-slate-900 mb-2">{t("reset.invalid_link_title")}</h2>
          <p className="text-sm text-slate-600 mb-5">{t("reset.invalid_link_desc")}</p>
          <Link to="/forgot-password" className="text-sm text-[#004EEB] hover:underline font-medium">
            {t("reset.request_new")}
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("reset.too_short"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("reset.no_match"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("reset.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-6 bg-slate-50/50" data-testid="reset-password-page">
      <div className="w-full max-w-md mec-card p-8">
        <Link to="/login" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-900 mb-5">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> {t("forgot.back_to_login")}
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900">{t("reset.title")}</div>
            <div className="text-xs text-slate-500">{t("reset.subtitle")}</div>
          </div>
        </div>

        {success ? (
          <div className="text-center py-6" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="font-display text-lg font-bold text-slate-900 mb-2">{t("reset.success_title")}</h3>
            <p className="text-sm text-slate-600">{t("reset.success_desc")}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-password" className="text-sm">{t("reset.new_password")}</Label>
              <PasswordInput
                id="new-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                data-testid="reset-password-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">{t("reset.hint")}</p>
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-sm">{t("reset.confirm_password")}</Label>
              <PasswordInput
                id="confirm-password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="reset-confirm-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#004EEB] hover:bg-[#003BCC] text-white"
              data-testid="reset-submit"
            >
              {loading ? t("reset.saving") : t("reset.save_button")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
